import React, { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useDeleteLinkedLayer } from '@/api/queries';
import { Button, ButtonType } from '@/common/button';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { TextIcon } from '@/common/text-icon';
import { toDecimal } from '@/utils';

import { useTemplateEditorStore } from '../store';
import { FieldsPanelView, type Layer, type TextLayerProps } from '../types';
import { FieldCard } from './field-card';
import { FieldCardLinkedLayers } from './field-card-linked-layers';

interface TextLayerEditViewProps {
    layer: Layer;
}

export const TextLayerEditView: FC<TextLayerEditViewProps> = ({ layer }) => {
    const textLayerProps = layer.props as TextLayerProps;
    const fields = useTemplateEditorStore((s) => s.template?.latestVersion.fields);
    const width = useTemplateEditorStore((s) => s.fieldsPanelWidth);
    const setSelectedLayer = useTemplateEditorStore((s) => s.setSelectedLayer);
    const setFields = useTemplateEditorStore((s) => s.setFields);
    const setFieldsPanelView = useTemplateEditorStore((s) => s.setFieldsPanelView);

    const { mutate: deleteLinkedLayer } = useDeleteLinkedLayer();
    const linkedField = fields ? fields.find((f) => f.linkedLayers?.find((l) => l.layerUuid === layer.id)) : null;

    const handleUnlinkField = () => {
        if (!linkedField || !fields) return; // should never happen, keeping typescript happy

        const linkedLayer = linkedField.linkedLayers?.find((l) => l.layerUuid === layer.id);
        if (linkedLayer) {
            deleteLinkedLayer(linkedLayer.id as number, {
                onSuccess: () => {
                    const updatedFields = fields.map((f) => {
                        const field = window.structuredClone(f);
                        if (f.id === linkedField.id) {
                            const linkedLayers = f.linkedLayers?.filter((l) => l.layerUuid !== linkedLayer.layerUuid);
                            field.linkedLayers = linkedLayers;
                        }
                        return field;
                    });
                    setFields(updatedFields);
                    closeView();
                },
            });
        }
    };

    const handleLinkField = () => {
        setFieldsPanelView(FieldsPanelView.TextLayerLink);
    };

    const handleNewField = () => {
        setFieldsPanelView(FieldsPanelView.TextFieldEdit);
    };

    const closeView = () => {
        setSelectedLayer(null);
        setFieldsPanelView(FieldsPanelView.Default);
    };

    return (
        <FloatingPanel
            className="pr-4 py-4 h-full flex-shrink-0 overflow-y-auto"
            type={FloatingPanelType.LIGHT}
            width={width}
        >
            <FloatingPanelItem className="flex flex-row justify-between items-center px-4">
                <div className="flex flex-row gap-2 items-center">
                    <TextIcon className="w-5 h-5 text-white" />
                    <p className="text-base text-white font-medium">Text Layer</p>
                </div>
                <button title="Cancel" className="text-white text-xs font-normal h-5" type="button" onClick={closeView}>
                    Cancel
                </button>
            </FloatingPanelItem>

            <FloatingPanelItem className="h-full px-4">
                <div className="flex flex-col gap-3">
                    <p className="text-sm text-white font-semibold">Details</p>
                    <div className="flex flex-row gap-2">
                        <div className="flex flex-col gap-1 w-[130px]">
                            <p className="text-sm text-gray-110 ">Dimensions</p>
                            <p className="text-sm text-gray-110 ">Position</p>
                            <p className="text-sm text-gray-110 ">Font</p>
                            <p className="text-sm text-gray-110 ">Font Size</p>
                            <p className="text-sm text-gray-110 ">Font Weight</p>
                            <p className="text-sm text-gray-110 ">Font Style</p>
                            <p className="text-sm text-gray-110 ">Fill</p>
                            <p className="text-sm text-gray-110 ">Line Height</p>
                        </div>
                        <div className="flex flex-col gap-1 w-full">
                            <p className="text-sm text-white ">
                                W {toDecimal(layer.rect.width, 2)}px H {toDecimal(layer.rect.height, 2)}px
                            </p>
                            <p className="text-sm text-white ">
                                X {toDecimal(layer.rect.x)} Y {toDecimal(layer.rect.y)}
                            </p>
                            <p className="text-sm text-white ">{textLayerProps.fontFamily}</p>
                            <p className="text-sm text-white ">{textLayerProps.fontSize}</p>
                            <p className="text-sm text-white ">{textLayerProps.fontWeight}</p>
                            <p className="text-sm text-white ">{textLayerProps.fontStyle}</p>
                            <p className="text-sm text-white ">{textLayerProps.fill}</p>
                            <p className="text-sm text-white ">{toDecimal(textLayerProps.lineHeight, 2)}</p>
                        </div>
                    </div>
                    <hr className="bg-gray-100 border-gray-100 w-full" />
                    <div className="flex flex-row justify-between">
                        <p className="text-sm text-white font-semibold">Content Field</p>
                        <p className={`text-sm ${linkedField ? 'text-green' : 'text-gray-110'} font-semibold`}>
                            {linkedField ? 'Linked' : 'Not Linked'}
                        </p>
                    </div>
                    {linkedField ? (
                        <FieldCard
                            field={linkedField}
                            linkedLayers={<FieldCardLinkedLayers linkedLayers={linkedField.linkedLayers} />}
                            button={
                                <Button
                                    title="Unlink"
                                    buttonType={ButtonType.SECONDARY}
                                    className="w-full"
                                    onClick={handleUnlinkField}
                                />
                            }
                        />
                    ) : fields && fields.length > 0 ? (
                        <Button title="Link to Text Field" buttonType={ButtonType.PRIMARY} onClick={handleLinkField} />
                    ) : (
                        <Button
                            title="Create New Text Field"
                            buttonType={ButtonType.PRIMARY}
                            onClick={handleNewField}
                        />
                    )}
                </div>
            </FloatingPanelItem>
        </FloatingPanel>
    );
};
